"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import { HomepageStatus } from "../types";

interface HomepageContextType {
    status: HomepageStatus;
    setStatus: (status: HomepageStatus) => void;
    readOnly: boolean;
    canEdit: boolean;
    isDirty: boolean;
    setIsDirty: (dirty: boolean) => void;
    lastSaved: Date | null;
    setLastSaved: (date: Date | null) => void;
}

const HomepageContext = createContext<HomepageContextType>({
    status: "draft",
    setStatus: () => { },
    readOnly: false,
    canEdit: true,
    isDirty: false,
    setIsDirty: () => { },
    lastSaved: null,
    setLastSaved: () => { },
});

export const useHomepageContext = () => React.useContext(HomepageContext);


interface HomepageProviderProps {
    children: React.ReactNode;
    initialStatus?: HomepageStatus;
    // Controlled state (optional)
    status?: HomepageStatus;
    setStatus?: (status: HomepageStatus) => void;
    canEdit?: boolean;
    isDirty?: boolean;
    setIsDirty?: (dirty: boolean) => void;
    lastSaved?: Date | null;
    setLastSaved?: (date: Date | null) => void;
}

export const HomepageProvider = ({
    children,
    initialStatus = "draft",
    status: controlledStatus,
    setStatus: controlledSetStatus,
    canEdit: controlledCanEdit,
    isDirty: controlledIsDirty,
    setIsDirty: controlledSetIsDirty,
    lastSaved: controlledLastSaved,
    setLastSaved: controlledSetLastSaved,
}: HomepageProviderProps) => {
    // Internal state
    const [localStatus, setLocalStatus] = useState<HomepageStatus>(initialStatus);
    const [localIsDirty, setLocalIsDirty] = useState(false);
    const [localLastSaved, setLocalLastSaved] = useState<Date | null>(null);

    // Resolve state (controlled vs local)
    const status = controlledStatus !== undefined ? controlledStatus : localStatus;
    const setStatus = controlledSetStatus || setLocalStatus;

    const isDirty = controlledIsDirty !== undefined ? controlledIsDirty : localIsDirty;
    const setIsDirty = controlledSetIsDirty || setLocalIsDirty;
    const canEdit = controlledCanEdit !== undefined ? controlledCanEdit : true;

    const lastSaved = controlledLastSaved !== undefined ? controlledLastSaved : localLastSaved;
    const setLastSaved = controlledSetLastSaved || setLocalLastSaved;

    const handleSetStatus = useCallback((newStatus: HomepageStatus) => {
        setStatus(newStatus);
    }, [setStatus]);

    return (
        <HomepageContext.Provider
            value={{
                status,
                setStatus: handleSetStatus,
                readOnly: status === "published" || status === "archived",
                canEdit,
                isDirty,
                setIsDirty,
                lastSaved,
                setLastSaved,
            }}
        >
            {children}
        </HomepageContext.Provider>
    );
};

export default HomepageContext;
